import React, { useState } from 'react';
import { Button } from '../../mawi-ui';
import { Input } from '../../mawi-ui';
import { Card, CardContent, CardHeader, CardTitle } from '../../mawi-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../mawi-ui';
import { Settings, RotateCcw } from 'lucide-react';

export interface ChatParameters {
  temperature: number;
  maxTokens: number;
  topP: number;
  presencePenalty: number;
  frequencyPenalty: number;
  systemMessage: string;
}

interface ChatParametersProps {
  parameters: ChatParameters;
  onChange: (parameters: ChatParameters) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const defaultParameters: ChatParameters = {
  temperature: 0.7,
  maxTokens: 2048,
  topP: 0.9,
  presencePenalty: 0.0,
  frequencyPenalty: 0.0,
  systemMessage: "You are a helpful AI assistant."
};

export function ChatParametersPanel({ parameters, onChange, isOpen, onToggle }: ChatParametersProps) {
  const [localParams, setLocalParams] = useState<ChatParameters>(parameters);

  const updateParameter = <K extends keyof ChatParameters>(key: K, value: ChatParameters[K]) => {
    const newParams = { ...localParams, [key]: value };
    setLocalParams(newParams);
    onChange(newParams);
  };

  const resetToDefaults = () => {
    setLocalParams(defaultParameters);
    onChange(defaultParameters);
  };

  if (!isOpen) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={onToggle}
            className="flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Parameters
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Configure advanced AI parameters like creativity, response length, and behavior controls.</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Card className="w-80 absolute bottom-full right-0 mb-2 z-50 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Chat Parameters</CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefaults}
              className="h-6 w-6 p-0"
              title="Reset to defaults"
            >
              <RotateCcw className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="h-6 w-6 p-0"
              title="Close"
            >
              ×
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Temperature */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="temperature" className="text-xs font-medium">
              Temperature
            </label>
            <span className="text-xs text-gray-500">{localParams.temperature.toFixed(1)}</span>
          </div>
          <input
            id="temperature"
            type="range"
            min={0}
            max={2}
            step={0.1}
            value={localParams.temperature}
            onChange={(e) => updateParameter('temperature', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <p className="text-xs text-gray-500">
            Controls randomness: 0 = deterministic, 2 = very creative
          </p>
        </div>

        {/* Max Tokens */}
        <div className="space-y-2">
          <label htmlFor="maxTokens" className="text-xs font-medium block">
            Max Tokens
          </label>
          <Input
            id="maxTokens"
            type="number"
            min={1}
            max={4096}
            value={localParams.maxTokens}
            onChange={(e) => updateParameter('maxTokens', parseInt(e.target.value) || 2048)}
            className="h-8 text-xs"
          />
          <p className="text-xs text-gray-500">
            Maximum response length (1-4096 tokens)
          </p>
        </div>

        {/* Top P */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="topP" className="text-xs font-medium">
              Top P
            </label>
            <span className="text-xs text-gray-500">{localParams.topP.toFixed(1)}</span>
          </div>
          <input
            id="topP"
            type="range"
            min={0}
            max={1}
            step={0.1}
            value={localParams.topP}
            onChange={(e) => updateParameter('topP', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <p className="text-xs text-gray-500">
            Nucleus sampling: 0.1 = focused, 1.0 = diverse
          </p>
        </div>

        {/* Presence Penalty */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="presencePenalty" className="text-xs font-medium">
              Presence Penalty
            </label>
            <span className="text-xs text-gray-500">{localParams.presencePenalty.toFixed(1)}</span>
          </div>
          <input
            id="presencePenalty"
            type="range"
            min={-2}
            max={2}
            step={0.1}
            value={localParams.presencePenalty}
            onChange={(e) => updateParameter('presencePenalty', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <p className="text-xs text-gray-500">
            Penalize new topics: -2 = encourage, +2 = discourage
          </p>
        </div>

        {/* Frequency Penalty */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="frequencyPenalty" className="text-xs font-medium">
              Frequency Penalty
            </label>
            <span className="text-xs text-gray-500">{localParams.frequencyPenalty.toFixed(1)}</span>
          </div>
          <input
            id="frequencyPenalty"
            type="range"
            min={-2}
            max={2}
            step={0.1}
            value={localParams.frequencyPenalty}
            onChange={(e) => updateParameter('frequencyPenalty', parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
          <p className="text-xs text-gray-500">
            Penalize repetition: -2 = encourage, +2 = discourage
          </p>
        </div>

        {/* System Message */}
        <div className="space-y-2">
          <label htmlFor="systemMessage" className="text-xs font-medium block">
            System Message
          </label>
          <textarea
            id="systemMessage"
            value={localParams.systemMessage}
            onChange={(e) => updateParameter('systemMessage', e.target.value)}
            className="w-full h-20 text-xs p-2 border rounded resize-none dark:bg-gray-800 dark:border-gray-600"
            placeholder="Enter system message..."
          />
          <p className="text-xs text-gray-500">
            Defines AI personality and behavior
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export { defaultParameters };